import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { CalendarClock, LoaderCircle, Phone, X } from "lucide-react";
import {
  STATUS_TRANSITIONS,
  formatClinicDate,
  formatClinicTime,
  toClinicDate,
  type Appointment,
  type AppointmentStatus,
  type Patient,
  type Practitioner,
  type Slot,
} from "@cop/domain";
import { api, key } from "../lib/api";
import { db, errorMessage, TENANT } from "../lib/firebase";
import { ACTION_LABEL, StatusPill } from "./StatusPill";
import { SlotChooser } from "./SlotChooser";

const CHANNEL: Record<string, string> = { web: "Web", assistant: "Asistente IA", admin: "Recepción", whatsapp: "WhatsApp", patient_portal: "Portal paciente" };

export function AppointmentPanel({ appt, practitioners, onClose }: { appt: Appointment; practitioners: Practitioner[]; onClose: () => void }) {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [busy, setBusy] = useState<AppointmentStatus | "reschedule" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  const [moving, setMoving] = useState(false);
  const [date, setDate] = useState(toClinicDate(new Date(appt.start)));
  const [slot, setSlot] = useState<Slot | null>(null);
  const prac = practitioners.find((p) => p.id === appt.practitionerId);

  useEffect(() => {
    setPatient(null);
    setCancelling(false);
    setMoving(false);
    setError(null);
    getDoc(doc(db, "tenants", TENANT, "patients", appt.patientId)).then((s) => s.exists() && setPatient({ id: s.id, ...s.data() } as Patient));
  }, [appt.id, appt.patientId]);

  async function setStatus(status: AppointmentStatus) {
    if (status === "cancelled" && !cancelling) return setCancelling(true);
    setBusy(status);
    setError(null);
    try {
      await api.status({ appointmentId: appt.id, status, reason: reason || undefined });
      setCancelling(false);
      setReason("");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function move() {
    if (!slot) return;
    setBusy("reschedule");
    setError(null);
    try {
      const hold = await api.hold({ serviceId: appt.serviceId, practitionerId: slot.practitionerId, chairId: slot.chairId, start: slot.start, idempotencyKey: key() });
      await api.reschedule({ appointmentId: appt.id, holdId: hold.holdId, idempotencyKey: key() });
      setMoving(false);
      setSlot(null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  const next = STATUS_TRANSITIONS[appt.status].filter((s) => s !== "cancelled");
  const canCancel = STATUS_TRANSITIONS[appt.status].includes("cancelled");

  return (
    <aside className="panel" aria-labelledby="panel-title">
      <header className="panel-head">
        <div>
          <p className="muted small cap">{formatClinicDate(appt.start)}</p>
          <h2 id="panel-title">
            <span className="mono">{formatClinicTime(appt.start)}–{formatClinicTime(appt.end)}</span> · {appt.patientName}
          </h2>
        </div>
        <button className="icon-btn" onClick={onClose} aria-label="Cerrar detalle">
          <X size={18} aria-hidden />
        </button>
      </header>

      <div className="panel-body">
        <StatusPill status={appt.status} />
        <dl className="kv">
          <dt>Prestación</dt>
          <dd>{appt.serviceName}</dd>
          <dt>Profesional</dt>
          <dd>
            <span className="dot" style={{ background: prac?.color }} aria-hidden /> {prac?.displayName ?? appt.practitionerId}
          </dd>
          <dt>Sillón</dt>
          <dd>{appt.chairId.replace("sillon-", "Sillón ")}</dd>
          <dt>Origen</dt>
          <dd>{CHANNEL[appt.channel] ?? appt.channel}</dd>
          {patient?.phone && (
            <>
              <dt>Teléfono</dt>
              <dd>
                <a href={`tel:${patient.phone.replace(/\s/g, "")}`} className="row gap-8 center">
                  <Phone size={14} aria-hidden /> {patient.phone}
                </a>
              </dd>
            </>
          )}
          {patient?.email && (
            <>
              <dt>Email</dt>
              <dd>{patient.email}</dd>
            </>
          )}
          {appt.notes && (
            <>
              <dt>Nota</dt>
              <dd>{appt.notes}</dd>
            </>
          )}
        </dl>

        {error && (
          <p className="notice notice-danger" role="alert">
            {error}
          </p>
        )}

        {next.length > 0 && (
          <div className="stack-sm">
            <p className="label">Siguiente paso</p>
            <div className="row wrap gap-8">
              {next.map((s, i) => (
                <button key={s} className={i === 0 ? "btn btn-primary" : "btn"} onClick={() => setStatus(s)} disabled={!!busy}>
                  {busy === s && <LoaderCircle className="spin" size={14} aria-hidden />}
                  {ACTION_LABEL[s]}
                </button>
              ))}
            </div>
          </div>
        )}

        {canCancel && !moving && (
          <div className="stack-sm">
            <div className="row wrap gap-8">
              <button className="btn" onClick={() => setMoving(true)} disabled={!!busy}>
                <CalendarClock size={14} aria-hidden /> Reprogramar
              </button>
              {!cancelling && (
                <button className="btn btn-danger-quiet" onClick={() => setStatus("cancelled")} disabled={!!busy}>
                  Cancelar turno
                </button>
              )}
            </div>
            {cancelling && (
              <div className="confirm-box">
                <label className="field">
                  <span>Motivo de cancelación</span>
                  <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej.: el paciente avisó por WhatsApp" autoFocus />
                </label>
                <div className="row gap-8">
                  <button className="btn btn-danger" onClick={() => setStatus("cancelled")} disabled={!!busy}>
                    {busy === "cancelled" && <LoaderCircle className="spin" size={14} aria-hidden />} Confirmar cancelación
                  </button>
                  <button className="btn" onClick={() => setCancelling(false)}>
                    Volver
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {moving && (
          <div className="stack-sm confirm-box">
            <p className="label">Nuevo horario</p>
            <SlotChooser serviceId={appt.serviceId} practitionerId="" date={date} onDate={setDate} value={slot} onChange={setSlot} practitioners={practitioners} />
            <div className="row gap-8">
              <button className="btn btn-primary" onClick={move} disabled={!slot || !!busy}>
                {busy === "reschedule" && <LoaderCircle className="spin" size={14} aria-hidden />} Mover turno
              </button>
              <button className="btn" onClick={() => setMoving(false)}>
                Volver
              </button>
            </div>
          </div>
        )}

        <p className="muted small">
          Creado por {appt.createdBy.split(":")[0]} · {new Date(appt.createdAt).toLocaleString("es-AR")}
        </p>
      </div>
    </aside>
  );
}
